import React, { useEffect, useMemo, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Database,
  FileCsv,
  MagnifyingGlass,
  ArrowsClockwise,
  Warning,
  ArrowRight,
  Calendar,
  Truck,
  Package,
  Rows,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { withAlpha, useReceptionStatuses } from "@/lib/receptionStatuses";
import { DateShortcuts } from "@/components/DateShortcuts";
import { mxDateToUtcISO } from "@/lib/dateShortcuts";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("es-MX", {
      timeZone: "America/Mexico_City",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch (err) {
    console.debug("[DatabaseView] date format failed", err);
    return iso;
  }
}

function dayKey(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("es-MX", {
      timeZone: "America/Mexico_City", year: "numeric", month: "2-digit", day: "2-digit",
    }).format(d);
  } catch {
    return iso.slice(0, 10);
  }
}

const GROUP_MODES = [
  { key: "none", label: "Todos", icon: Rows },
  { key: "day", label: "Por Día", icon: Calendar },
  { key: "provider", label: "Por Proveedor", icon: Truck },
  { key: "sku", label: "Por SKU", icon: Package },
];

export default function DatabaseView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [providers, setProviders] = useState([]);
  const [creators, setCreators] = useState([]);
  const { list: receptionList } = useReceptionStatuses();
  const [groupMode, setGroupMode] = useState("none");
  const [skusList, setSkusList] = useState("");
  const [filters, setFilters] = useState({
    provider_id: "",
    reception_status: "",
    created_by: "",
    date_from: "",
    date_to: "",
    min_faltantes: "",
    only_rejected: false,
  });

  const buildParams = () => {
    const params = {};
    if (filters.provider_id) params.provider_id = filters.provider_id;
    if (filters.reception_status) params.reception_status = filters.reception_status;
    if (filters.created_by) params.created_by = filters.created_by;
    if (filters.date_from) params.date_from = mxDateToUtcISO(filters.date_from, false);
    if (filters.date_to) params.date_to = mxDateToUtcISO(filters.date_to, true);
    if (filters.min_faltantes !== "" && Number(filters.min_faltantes) > 0) {
      params.min_faltantes = Number(filters.min_faltantes);
    }
    if (filters.only_rejected) params.only_rejected = true;
    if (skusList.trim()) params.skus = skusList.trim();
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/database", { params: buildParams() });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([
          api.get("/catalogs/providers"),
          api.get("/reports/creators"),
        ]);
        setProviders(p.data);
        setCreators(c.data || []);
      } catch (err) {
        console.debug("[DatabaseView] failed to load filter data", err);
      }
    })();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.provider_id, filters.reception_status, filters.created_by,
    filters.date_from, filters.date_to, filters.min_faltantes, filters.only_rejected,
    skusList,
  ]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => {
      const hay = [
        r.folio, r.provider_name, r.sku, r.vpn, r.upc, r.brand, r.br, r.po, r.reception_label,
      ]
        .filter(Boolean).join(" | ").toLowerCase();
      return hay.includes(term);
    });
  }, [rows, q]);

  const totals = useMemo(() => {
    let expected = 0, received = 0, missing = 0, rejected = 0;
    for (const r of filtered) {
      expected += Number(r.qty_expected || 0);
      received += Number(r.qty_received || 0);
      missing += Number(r.faltantes || 0);
      if (r.rejected) rejected += 1;
    }
    return { expected, received, missing, rejected, count: filtered.length };
  }, [filtered]);

  const grouped = useMemo(() => {
    if (groupMode === "none") return null;
    const map = new Map();
    const skipNoSku = groupMode === "sku";
    for (const r of filtered) {
      if (skipNoSku && r.sku === "— sin SKUs —") continue;
      const key =
        groupMode === "day" ? dayKey(r.appointment_datetime) :
        groupMode === "provider" ? (r.provider_name || "—") :
        (r.sku || "—");
      const existing = map.get(key) || {
        key,
        rows: 0,
        appointments: new Set(),
        expected: 0,
        received: 0,
        missing: 0,
        rejected: 0,
        // for provider mode we also track top sku missing; simple version below
      };
      existing.rows += 1;
      if (r.appointment_id) existing.appointments.add(r.appointment_id);
      existing.expected += Number(r.qty_expected || 0);
      existing.received += Number(r.qty_received || 0);
      existing.missing += Number(r.faltantes || 0);
      if (r.rejected) existing.rejected += 1;
      map.set(key, existing);
    }
    return Array.from(map.values())
      .map((g) => ({ ...g, appointments: g.appointments.size }))
      .sort((a, b) => {
        if (groupMode === "day") return a.key < b.key ? 1 : -1; // recent first
        return b.missing - a.missing || b.rows - a.rows;
      });
  }, [filtered, groupMode]);

  const clearFilters = () => {
    setFilters({
      provider_id: "", reception_status: "", created_by: "",
      date_from: "", date_to: "", min_faltantes: "", only_rejected: false,
    });
    setSkusList("");
    setQ("");
  };

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      const p = buildParams();
      for (const [k, v] of Object.entries(p)) params.set(k, String(v));
      const token = localStorage.getItem("rcn_token");
      const res = await fetch(`${API}/reports/database.csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "base_de_datos.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.debug("[DatabaseView] CSV download failed", err);
      toast.error("No se pudo descargar el CSV");
    }
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) =>
    k === "only_rejected" ? v : v !== "" && v !== false
  ).length + (skusList.trim() ? 1 : 0);

  return (
    <div>
      <div className="mb-6">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-[#0055FF] mb-2">
          Base de Datos · Consolidado
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl flex items-center gap-3">
              <Database size={36} weight="duotone" className="text-[#0055FF]" />
              Base de Datos
            </h1>
            <p className="text-white/50 text-sm mt-2 max-w-2xl">
              Vista granular de todas las citas con sus SKUs, cantidades esperadas vs recibidas y
              faltantes calculados en tiempo real.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              data-testid="db-refresh-btn"
              className="flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
            >
              <ArrowsClockwise size={14} /> Refrescar
            </button>
            <button
              onClick={downloadCsv}
              data-testid="db-download-csv-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-sm bg-[#10B981]/15 border border-[#10B981]/40 text-[#10B981] text-sm font-semibold hover:bg-[#10B981]/25 transition-colors"
            >
              <FileCsv size={16} weight="fill" /> Descargar CSV
            </button>
          </div>
        </div>
      </div>

      {/* Filters card */}
      <div className="bg-[#111111] border border-white/10 rounded-sm p-4 mb-4 space-y-4">
        <div>
          <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-2">
            Rango de fechas
          </label>
          <DateShortcuts
            from={filters.date_from}
            to={filters.date_to}
            onChange={({ from, to }) => setFilters((f) => ({ ...f, date_from: from, date_to: to }))}
            testId="db-date-shortcuts"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
              Proveedor
            </label>
            <select
              data-testid="db-filter-provider"
              value={filters.provider_id}
              onChange={(e) => setFilters((f) => ({ ...f, provider_id: e.target.value }))}
              className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none"
            >
              <option value="">Todos</option>
              {providers.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
              Recepción
            </label>
            <select
              data-testid="db-filter-reception"
              value={filters.reception_status}
              onChange={(e) => setFilters((f) => ({ ...f, reception_status: e.target.value }))}
              className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none"
            >
              <option value="">Todas</option>
              {receptionList.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
              Creado por
            </label>
            <select
              data-testid="db-filter-created-by"
              value={filters.created_by}
              onChange={(e) => setFilters((f) => ({ ...f, created_by: e.target.value }))}
              className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none"
            >
              <option value="">Todos</option>
              {creators.map((u) => (<option key={u} value={u}>{u}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
              Faltantes mínimos
            </label>
            <input
              type="number"
              min="0"
              data-testid="db-filter-min-faltantes"
              value={filters.min_faltantes}
              onChange={(e) => setFilters((f) => ({ ...f, min_faltantes: e.target.value }))}
              placeholder="ej. 10"
              className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
              Buscar en tabla
            </label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="db-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Folio · SKU · Marca · Proveedor…"
                className="w-full pl-8 pr-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white placeholder:text-white/30 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-1">
              SKUs (separados por coma)
            </label>
            <input
              data-testid="db-skus-list"
              value={skusList}
              onChange={(e) => setSkusList(e.target.value)}
              placeholder="SKU-001, SKU-002…"
              className="w-full px-2 py-2 rounded-sm bg-[#0A0A0A] border border-white/10 text-xs text-white placeholder:text-white/30 focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="inline-flex items-center gap-2 text-xs text-white/70 cursor-pointer">
              <input
                data-testid="db-only-rejected"
                type="checkbox"
                checked={filters.only_rejected}
                onChange={(e) => setFilters((f) => ({ ...f, only_rejected: e.target.checked }))}
                className="accent-[#FF3B30]"
              />
              Solo rechazos
            </label>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                data-testid="db-clear-filters"
                className="text-[10px] font-mono-data uppercase tracking-widest text-white/50 hover:text-[#FF3B30] transition-colors"
              >
                Limpiar filtros ({activeFilterCount})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Grouping tabs */}
      <div className="flex flex-wrap items-center gap-1 mb-4">
        {GROUP_MODES.map((g) => {
          const isActive = groupMode === g.key;
          const GIcon = g.icon;
          return (
            <button
              key={g.key}
              onClick={() => setGroupMode(g.key)}
              data-testid={`db-group-${g.key}`}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-mono-data uppercase tracking-widest border transition-colors ${
                isActive
                  ? "bg-[#0055FF] border-[#0055FF] text-white"
                  : "bg-[#0A0A0A] border-white/10 text-white/60 hover:text-white hover:border-white/30"
              }`}
            >
              <GIcon size={13} /> {g.label}
            </button>
          );
        })}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <MiniKpi label="Filas" value={totals.count} testId="db-kpi-count" />
        <MiniKpi label="Esperado" value={totals.expected.toLocaleString()} testId="db-kpi-expected" />
        <MiniKpi label="Recibido" value={totals.received.toLocaleString()} testId="db-kpi-received" tone="success" />
        <MiniKpi label="Faltantes" value={totals.missing.toLocaleString()} testId="db-kpi-missing" tone="warning" />
        <MiniKpi label="Rechazos" value={totals.rejected} testId="db-kpi-rejected" tone="error" />
      </div>

      {/* Table */}
      <div className="bg-[#111111] border border-white/10 rounded-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[65vh]">
          {groupMode === "none" ? (
            <FlatTable rows={filtered} loading={loading} />
          ) : (
            <GroupedTable
              rows={grouped || []}
              loading={loading}
              mode={groupMode}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function FlatTable({ rows, loading }) {
  return (
    <table className="w-full text-xs">
      <thead className="text-[9px] font-mono-data uppercase tracking-widest text-white/40 sticky top-0 bg-[#111111] z-10">
        <tr className="border-b border-white/10">
          <th className="text-left px-3 py-2">Folio</th>
          <th className="text-left px-3 py-2">Fecha</th>
          <th className="text-left px-3 py-2">Proveedor</th>
          <th className="text-left px-3 py-2">Recepción</th>
          <th className="text-left px-3 py-2">SKU</th>
          <th className="text-left px-3 py-2">Marca</th>
          <th className="text-left px-3 py-2">PO / BR</th>
          <th className="text-left px-3 py-2">Creado por</th>
          <th className="text-right px-3 py-2">Esp.</th>
          <th className="text-right px-3 py-2">Rec.</th>
          <th className="text-right px-3 py-2">Falt.</th>
          <th className="text-center px-3 py-2">Rechazo</th>
          <th className="text-right px-3 py-2"></th>
        </tr>
      </thead>
      <tbody data-testid="db-rows">
        {loading ? (
          <tr><td colSpan={13} className="p-6 text-white/50 text-center">Cargando…</td></tr>
        ) : rows.length === 0 ? (
          <tr><td colSpan={13} className="p-10 text-white/50 text-center">
            Sin resultados. Ajusta los filtros o crea una cita.
          </td></tr>
        ) : (
          rows.map((r, i) => {
            const missing = Number(r.faltantes || 0);
            const rowTint = r.rejected ? "bg-[#FF3B30]/5" : missing > 0 ? "bg-[#F59E0B]/5" : "";
            return (
              <tr
                key={`${r.appointment_id}-${r.product_id || i}`}
                className={`border-b border-white/5 hover:bg-white/5 transition-colors ${rowTint}`}
              >
                <td className="px-3 py-2 font-mono-data text-[#0055FF] font-bold">{r.folio}</td>
                <td className="px-3 py-2 text-white/70 whitespace-nowrap">{fmtDate(r.appointment_datetime)}</td>
                <td className="px-3 py-2 text-white/80 truncate max-w-[160px]" title={r.provider_name}>
                  {r.provider_name}
                </td>
                <td className="px-3 py-2">
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm font-mono-data text-[9px] uppercase tracking-widest border"
                    style={{
                      color: r.reception_color,
                      backgroundColor: withAlpha(r.reception_color, 0.14),
                      borderColor: `${r.reception_color}55`,
                    }}
                  >
                    {r.reception_label}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono-data text-white truncate max-w-[160px]" title={r.sku}>
                  {r.sku || "—"}
                </td>
                <td className="px-3 py-2 text-white/80 truncate max-w-[120px]" title={r.brand}>{r.brand || "—"}</td>
                <td className="px-3 py-2 font-mono-data text-white/60 whitespace-nowrap">
                  {r.po || "—"} {r.br ? `· ${r.br}` : ""}
                </td>
                <td className="px-3 py-2 text-white/60 truncate max-w-[100px]" title={r.created_by}>{r.created_by || "—"}</td>
                <td className="px-3 py-2 text-right font-mono-data text-white/80">{r.qty_expected || 0}</td>
                <td className="px-3 py-2 text-right font-mono-data text-[#10B981] font-semibold">{r.qty_received || 0}</td>
                <td className="px-3 py-2 text-right font-mono-data font-bold">
                  {missing > 0 ? <span className="text-[#F59E0B]">{missing}</span> : <span className="text-white/30">0</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.rejected ? (
                    <span className="inline-flex items-center gap-1 text-[#FF3B30] font-mono-data text-[10px]">
                      <Warning size={11} weight="fill" /> SÍ
                    </span>
                  ) : (
                    <span className="text-white/30">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <Link
                    to={`/citas/${r.appointment_id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-widest text-[#0055FF] hover:text-white transition-colors"
                  >
                    Abrir <ArrowRight size={10} />
                  </Link>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

function GroupedTable({ rows, loading, mode }) {
  const label =
    mode === "day" ? "Día" :
    mode === "provider" ? "Proveedor" : "SKU";
  return (
    <table className="w-full text-xs">
      <thead className="text-[9px] font-mono-data uppercase tracking-widest text-white/40 sticky top-0 bg-[#111111] z-10">
        <tr className="border-b border-white/10">
          <th className="text-left px-3 py-2">{label}</th>
          <th className="text-right px-3 py-2">Citas</th>
          <th className="text-right px-3 py-2">Filas SKU</th>
          <th className="text-right px-3 py-2">Esperado</th>
          <th className="text-right px-3 py-2">Recibido</th>
          <th className="text-right px-3 py-2">Faltantes</th>
          <th className="text-right px-3 py-2">Rechazos</th>
          <th className="text-right px-3 py-2">Cumplim.</th>
        </tr>
      </thead>
      <tbody data-testid="db-grouped-rows">
        {loading ? (
          <tr><td colSpan={8} className="p-6 text-white/50 text-center">Cargando…</td></tr>
        ) : rows.length === 0 ? (
          <tr><td colSpan={8} className="p-10 text-white/50 text-center">
            Sin resultados para esta agrupación.
          </td></tr>
        ) : (
          rows.map((g) => {
            const pct = g.expected > 0 ? Math.round((g.received / g.expected) * 100) : 0;
            const missing = Number(g.missing || 0);
            const rowTint =
              g.rejected > 0 ? "bg-[#FF3B30]/5" :
              missing > 0 ? "bg-[#F59E0B]/5" : "";
            return (
              <tr key={g.key} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${rowTint}`}>
                <td className="px-3 py-2 font-mono-data text-white font-semibold">{g.key}</td>
                <td className="px-3 py-2 text-right font-mono-data text-white/80">{g.appointments}</td>
                <td className="px-3 py-2 text-right font-mono-data text-white/60">{g.rows}</td>
                <td className="px-3 py-2 text-right font-mono-data text-white/80">{g.expected.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono-data text-[#10B981] font-semibold">{g.received.toLocaleString()}</td>
                <td className="px-3 py-2 text-right font-mono-data font-bold">
                  {missing > 0 ? <span className="text-[#F59E0B]">{missing.toLocaleString()}</span> : <span className="text-white/30">0</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono-data">
                  {g.rejected > 0 ? <span className="text-[#FF3B30]">{g.rejected}</span> : <span className="text-white/30">0</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono-data">
                  <span className={pct >= 100 ? "text-[#10B981]" : pct >= 80 ? "text-white" : "text-[#F59E0B]"}>
                    {g.expected > 0 ? `${pct}%` : "—"}
                  </span>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

function MiniKpi({ label, value, tone = "default", testId }) {
  const tones = {
    default: "text-white",
    success: "text-[#10B981]",
    warning: "text-[#F59E0B]",
    error: "text-[#FF3B30]",
  };
  return (
    <div data-testid={testId} className="bg-[#111111] border border-white/10 rounded-sm p-3">
      <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-mono-data font-bold text-2xl ${tones[tone]}`}>{value}</div>
    </div>
  );
}
